import React, { useState } from 'react';
import { router } from '@inertiajs/react';
import SignupLayout from '../Layout.jsx';
import RadioCard from '../../../components/RadioCard.jsx';
import { primaryBtnClass, secondaryBtnClass, selectClass, labelClass, saveDraftPatch } from '../shared.jsx';

// Keep in sync with Cohort::LIVE_TRAINING_TYPES (app/models/cohort.rb): both the
// 40-hour full recertification and the standalone 20-hour live SKU seat the
// buyer in a live cohort, so both must collect a cohort month here.
const LIVE_COHORT_TRAINING_TYPES = ['recertification', 'recertification_20hr_live'];

const TRAINING_OPTIONS = {
  Administrator: [
    {
      value: 'recertification',
      label: 'Full Recertification — 40 Hours',
      badge: 'Most Complete',
      description: 'Everything you need to fully recertify your RCFE or ARF license.',
      meta: ['20 hrs Live', '20 hrs Self-Paced', 'CDSS Approved'],
    },
    {
      value: 'recertification_20hr_live',
      label: 'Live Classes Only — 20 Hours',
      badge: 'Live Cohort',
      badgeVariant: 'teal',
      description: 'Join the 4-day live curriculum with CA-licensed faculty.',
      meta: ['RCFE or ARF', 'Instructor-led'],
    },
    {
      value: 'recertification_20hr',
      label: 'Self-Paced Only — 20 Hours',
      description: 'Complete Title 22 coursework on your own schedule.',
      meta: ['RCFE or ARF', 'On-demand'],
    },
  ],
  Caregiver: [
    { value: 'initial_training',      label: 'Initial Training',      description: '40 hours' },
    { value: 'continuing_education',  label: 'Annual Training',       description: '20 hours' },
  ],
};

export default function IndividualTraining({ role, training_type: initialTrainingType, cohort_months: cohortMonths = [], cohort_month: initialCohortMonth, progress }) {
  const [trainingType, setTrainingType] = useState(initialTrainingType || '');
  const [cohortMonth, setCohortMonth] = useState(initialCohortMonth || '');
  const [submitting, setSubmitting] = useState(false);

  const isLive = LIVE_COHORT_TRAINING_TYPES.includes(trainingType);
  const cohortMissing = isLive && !cohortMonth;
  const canContinue = !!trainingType && !cohortMissing && !submitting;

  const handleContinue = async () => {
    if (!canContinue) return;
    setSubmitting(true);
    await saveDraftPatch({ individual: { training_type: trainingType, cohort_month: isLive ? cohortMonth : null } });
    router.visit('/signup/individual/facility');
  };

  const isAdmin = role === 'Administrator';

  return (
    <SignupLayout progress={progress} maxWidth="sm:max-w-md">
      <div className="space-y-4">
        <div className="text-center space-y-2">
          <h2 className="text-2xl/9 font-bold tracking-tight text-gray-900">
            {isAdmin ? 'Which recertification path fits you?' : 'What type of training do you need?'}
          </h2>
          {isAdmin && (
            <p className="text-sm text-gray-500">Pick the option that matches what you need to renew.</p>
          )}
        </div>
        <fieldset className="space-y-3 mt-6">
          {(TRAINING_OPTIONS[role] || []).map((opt) => (
            <RadioCard key={opt.value} name="training_type" value={opt.value}
              checked={trainingType === opt.value} onChange={() => setTrainingType(opt.value)}
              label={opt.label} badge={opt.badge} badgeVariant={opt.badgeVariant}
              description={opt.description} meta={opt.meta} />
          ))}
        </fieldset>
        {isLive && (
          <div className="mt-4 rounded-lg bg-teal-50 ring-1 ring-inset ring-teal-100 p-4">
            <label htmlFor="cohort_month" className={labelClass}>Choose your cohort</label>
            <p className="mt-1 text-sm text-gray-500">Live classes are scheduled by monthly cohort. Pick the month you'll attend.</p>
            <select id="cohort_month" name="cohort_month" value={cohortMonth}
              onChange={(e) => setCohortMonth(e.target.value)} className={`${selectClass} mt-2`}>
              <option value="" disabled>Select a month…</option>
              {cohortMonths.map((month) => (
                <option key={month} value={month}>{month}</option>
              ))}
            </select>
          </div>
        )}
        <div className="flex gap-3 mt-6">
          <button type="button" onClick={() => router.visit('/signup/individual/role')} className={`${secondaryBtnClass} flex-1`}>Back</button>
          <button type="button" onClick={handleContinue} disabled={!canContinue} className={`${primaryBtnClass} flex-1`}>Continue</button>
        </div>
      </div>
    </SignupLayout>
  );
}
